/* eslint-disable */
// All ESLint errors will be ignored in this file
"use client"

import {
  Building2,
  CheckCircle,
  Clock,
  Globe,
  Loader2,
  Mail,
  Search,
  XCircle,
  Loader,
  Download
} from "lucide-react";
import { useRef, useEffect, useState } from "react";

import {
  Badge,
  Button,
  Input,
  Progress,
  Table
} from 'reactstrap';


import { Card, CardContent, CardHeader, CardTitle } from "../../components/card";
import { HOST_URL } from '../../../../constants/globals';
import promptService from '../../../../services/PromptService';


export default function SingleAccountResearchApp() {
  const [searchQuery, setSearchQuery] = useState("")
  const [isResearching, setIsResearching] = useState(false)
  const [currentTask, setCurrentTask] = useState("")
  const [progress, setProgress] = useState(0)
  const [tasks, setTasks] = useState([])
  const [researchData, setResearchData] = useState({})
  const [completedTasks, setCompletedTasks] = useState([])
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  

  const getUseCaseParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('use_case');
  };

  const sectionRefs = useRef({})

    useEffect(() => {
    const fetchData = async () => {
      console.log('fetching');
      setIsLoadingTasks(true);
      const result = await promptService.getPromptListForAccount(getUseCaseParam());
      const data = result.map(task => ({ ...task, status: "pending" }))
      setTasks(data);
      setIsLoadingTasks(false);
    };

    fetchData();
  }, []);

  const fetchTaskResearchAsync = async (task, inputUrl, sessionId) => {

    const result = await promptService.askPromptWithPrerequisites(task, inputUrl, sessionId);
    if (!result.jobId) throw new Error("Failed to submit research job");
    return result.jobId;
  };

  const pollJobResult = async (jobId, interval = 2000, maxAttempts = 60) => {
    let attempts = 0;
    while (attempts < maxAttempts) {
      const res = await fetch(`${HOST_URL}/api/prompts/job-result/${jobId}`);
      const data = await res.json();
      if (data.status !== "PENDING") {
        return data;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
      attempts++;
    }
    throw new Error("Job timed out");
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsResearching(true)
    setProgress(0)
    setResearchData({})
    setCompletedTasks([])
    setTasks(prevTasks => prevTasks.map(task => ({ ...task, status: "pending" })))

    let localSessionId = null;
    const currentTasks = [...tasks]

    // Simulate research progress one task at a time
    for (let i = 0; i < currentTasks.length; i++) {
      const task = currentTasks[i];
      setCurrentTask(`Processing ${task.name}...`);

      // Update task status to in-progress
      setTasks((prevTasks) => prevTasks.map((t) => (t.id === task.id ? { ...t, status: "running" } : t)));

      // 2 minutes = 120 seconds, update progress every 2 seconds
      const totalDuration = 120 * 1000 // 2 minutes in milliseconds
      const updateInterval = 2000 // Update every 2 seconds
      const totalUpdates = totalDuration / updateInterval

      let currentUpdate = 0

      // Create smooth progress updates for this step
      const progressInterval = setInterval(() => {
        currentUpdate++
        const stepProgress = (currentUpdate / totalUpdates) * 100
        const overallProgress = ((i + stepProgress / 100) / currentTasks.length) * 100

        setProgress(overallProgress)

        // Update current task with time remaining
        const timeRemaining = Math.ceil((totalDuration - currentUpdate * updateInterval) / 1000)
        const minutesRemaining = Math.floor(timeRemaining / 60)
        const secondsRemaining = timeRemaining % 60

        setCurrentTask(
          `Processing ${task.name}... (~${minutesRemaining}:${secondsRemaining.toString().padStart(2, "0")} remaining)`,
        )

        if (currentUpdate >= totalUpdates) {
          clearInterval(progressInterval)
        }
      }, updateInterval)

      try {
        
        const jobId = await fetchTaskResearchAsync(task, searchQuery, localSessionId);
        const taskResult = await pollJobResult(jobId);
        
        localSessionId = taskResult.sessionId
        // Add this task's data to the research results
        setResearchData((prevData) => ({
          ...prevData,
          [task.name]: taskResult.data,
        }));

        clearInterval(progressInterval)
        
      } catch (error) {
        // Handle errors by marking the task as failed
        console.error(`Error processing task ${task.name}:`, error);
        setResearchData((prevData) => ({
          ...prevData,
          [task.name]: {"result": taskResult.rawData},
        }));
      }
      
      setTasks((prevTasks) => prevTasks.map((t) => (t.id === task.id ? { ...t, status: "completed" } : t)));
      setCompletedTasks((prev) => [...prev, task.name]);
      setProgress(((i + 1) / currentTasks.length) * 100);
    }

    setIsResearching(false)
    setCurrentTask("Research completed!")
  }

  const scrollToSection = (taskName) => {
    if (!researchData[taskName]) return // Only allow navigation if research for this task is completed

    const element = sectionRefs.current[taskName]
    if (element) {
      // Get the element's position relative to the viewport
      const elementRect = element.getBoundingClientRect();
      
      // Get current scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calculate the absolute position and apply offset for header (adjust the 60px as needed)
      const offsetPosition = scrollTop + elementRect.top - 60;

      // Scroll to the element with offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 mt-1 text-green-500" />
      case "running":
        return <Loader2 className="h-4 w-4 mt-1 text-blue-500 animate-spin" />
      case "pending":
        return <Clock className="h-4 w-4 mt-1 text-gray-400"/>
      case "failed":
        return <XCircle className="h-4 w-4 mt-1 text-red-500" />
      default:
        return <div className="h-4 w-4 mt-1 rounded-full border-2 border-gray-300" />
    }
  }

  const isValidUrl = (string) => {
    // Regex for URL validation that accepts all formats you specified
    const urlPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
    return urlPattern.test(string);
  };

  const isValidEmail = (string) => {
    // Regex for email validation
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(string);
  };

  // Add a state for validation
  const [isValidInput, setIsValidInput] = useState(true);

  // Update the onChange handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Only validate if there's text input
    if (value.trim()) {
      setIsValidInput(isValidUrl(value) || isValidEmail(value));
    } else {
      setIsValidInput(true); // Empty input is considered valid (just disabled button)
    }
  };

  // Function to convert research data to CSV
  const downloadCSV = () => {
    if (Object.keys(researchData).length === 0) return

    const csvRows = []

    // Add header row
    csvRows.push(["Research Task", "Field", "Value"])

    // Process each research task
    Object.entries(researchData).forEach(([taskName, taskData]) => {
      const flattenData = (data, prefix = "") => {
        Object.entries(data).forEach(([key, value]) => {
          const fieldName = prefix ? `${prefix}.${key}` : key

          if (typeof value === "object" && value !== null && !Array.isArray(value)) {
            // Recursively flatten nested objects
            flattenData(value, fieldName)
          } else if (Array.isArray(value)) {
            // Handle arrays
            if (value.length > 0 && typeof value[0] === "object") {
              // Array of objects
              value.forEach((item, index) => {
                if (typeof item === "object") {
                  flattenData(item, `${fieldName}[${index}]`)
                } else {
                  csvRows.push([taskName, `${fieldName}[${index}]`, String(item)])
                }
              })
            } else {
              // Array of primitives
              csvRows.push([taskName, fieldName, value.join("; ")])
            }
          } else {
            // Primitive values
            csvRows.push([taskName, fieldName, String(value)])
          }
        })
      }

      flattenData(taskData)
    })

    // Convert to CSV string
    const csvContent = csvRows
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))
      .join("\n")

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)

    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `account-research-${searchQuery.replace(/[^a-z0-9]/gi, "_").toLowerCase()}-${new Date().toISOString().split("T")[0]}.csv`,
    )
    link.style.visibility = "hidden"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderJsonTable = (data, parentKey = "") => {
  // Handle primitive values
  if (typeof data !== "object" || data === null) {
    if (isValidUrl(String(data))) {
      // Format URL with protocol if missing
      const url = String(data).startsWith('http') ? String(data) : `https://${String(data)}`;
      return (
        <a 
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {String(data)}
        </a>
      );
    } else {
      return <span className="research-table-text">{String(data)}</span>;
    }
  }

  // Special handling for top-level object - render as sections with headings
  if (parentKey === "") {
    return (
      <div className="space-y-6">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="section">
            <h2 className="text-xl font-bold mb-4 text-blue-700 pb-2">
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </h2>
            {renderJsonTable(value, key)}
          </div>
        ))}
      </div>
    );
  }

  // Handle arrays
  if (Array.isArray(data)) {
    // If it's an array of objects with the same structure, render as a table with headers
    if (data.length > 0 && typeof data[0] === "object" && data[0] !== null && !Array.isArray(data[0])) {
      // Get keys from first object to use as headers
      const headers = Object.keys(data[0]);
      
      return (
        <div className="overflow-x-auto">
          <Table className="min-w-full divide-y divide-gray-200" hover responsive>
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header) => (
                  <th 
                    key={header} 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((header) => (
                    <td key={`${rowIndex}-${header}`} className="px-4 py-3 text-sm research-table-text">
                      {renderJsonTable(item[header], `${parentKey}[${rowIndex}].${header}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }
    
    // Fallback for arrays of non-objects or mixed content
    return (
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="bg-gray-50 p-2 rounded border-l-2 border-blue-200">
            {typeof item === "object" ? renderJsonTable(item, `${parentKey}[${index}]`) : String(item)}
          </div>
        ))}
      </div>
    );
  }

  // For regular objects, keep the current key-value format
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-full divide-y divide-gray-200" hover responsive>
        <tbody className="bg-white divide-y divide-gray-200">
          {Object.entries(data).map(([key, value]) => (
            <tr key={key}>
              <td className="px-4 py-3 text-sm font-medium research-table-text bg-gray-50 header-td">
                {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </td>
              <td className="px-4 py-3 text-sm research-table-text">{renderJsonTable(value, key)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Account Research</h1>
            </div>
            <Badge variant="secondary" className="text-sm">
              AI-Powered Research Tool
            </Badge>
          </div>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Automated account research tool that uses the power of AI to scrape the web pages, search on web or query
            database, and aggregate information about prospects.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {isResearching ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      <span className='pl-2'>Scanning...</span>
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      <span className='pl-2'>Research Tasks</span>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {tasks.map((task) => {
                const isClickable = task.status === "completed" && researchData[task.name]
                return (
                  <div
                      key={task.id}
                      className={`flex items-start space-x-3 ${isClickable ? 'cursor-pointer' : ''}`}
                      onClick={() => isClickable && scrollToSection(task.name)}
                    >
                    {getStatusIcon(task.status)}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          task.status === "completed"
                            ? "text-green-700"
                            : task.status === "running"
                              ? "text-blue-700"
                              : "text-gray-500"
                        }`}
                      >
                        {task.name}
                      </p>
                    </div>
                  </div>
                )})}

                {/* Progress Bar */}
                {/* {isResearching && (
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>Progress</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-gray-500 mt-2">
                      {progress < 100 ? `${Math.round((100 - progress) * 0.3)} seconds remaining` : "Complete!"}
                    </p>
                  </div>
                )} */}
                {isResearching && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                    <span className="text-sm font-medium text-blue-900">Researching...</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-blue-700">{currentTask}</p>
                </div>
              )}

                {/* Navigation Helper */}
                {Object.keys(researchData).length > 0 && !isResearching && (
                  <div className="mt-6 space-y-4">
                    <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-600">
                          {Object.keys(researchData).length === tasks.length
                            ? "Research Complete!"
                            : `${Object.keys(researchData).length}/${tasks.length} Tasks Complete`}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">Click on any completed task above to jump to its results.</p>
                    </div>

                    <Button
                      onClick={downloadCSV}
                      className="w-full btn-primary success"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download CSV Report</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search Section */}
            {Object.keys(researchData).length == 0 && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div>
                      <p className="mb-2 block">
                        To begin the research, please provide me with the Email or Website URL
                      </p>

                      <div className="flex space-x-2">
                        <div className="flex-1 relative">
                          <Input
                            type="text"
                            placeholder="Enter company email or website URL..."
                            value={searchQuery}
                            onChange={handleInputChange}
                            className={`pl-10 ${!isValidInput ? 'border-danger' : ''}`}
                          />
                          {!isValidInput && (
                            <div className="text-danger text-xs mt-1">
                              Please enter a valid website URL (e.g., google.com) or email address
                            </div>
                          )}
                          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 icon-in-input" />
                        </div>
                        <Button onClick={handleSearch} disabled={isResearching || !searchQuery.trim() || !isValidInput} className="px-6 mb-0">
                          {isResearching ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Researching...
                            </>
                          ) : (
                            "Start Research"
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Mail className="h-4 w-4" />
                        <span>Email</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Globe className="h-4 w-4" />
                        <span>Website URL</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {Object.keys(researchData).length === 0 && !isResearching && (
              <Card>
                <CardContent className="pt-12 pb-12 text-center">
                  <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Research</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Enter a company email or website URL above to start your comprehensive account research. Our AI will
                    gather insights across multiple data sources.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Results Section - Individual Boxes for Each Research Task */}
            
            {Object.keys(researchData).length > 0 && (
              <div className="space-y-6">
                {completedTasks.map((taskName) => {
                  const task = tasks.find((t) => t.name === taskName)
                  const taskData = researchData[taskName]

                  return (
                    <div 
                      key={task.id} 
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6 mb-8"
                      ref={(el) => {
                        sectionRefs.current[taskName] = el
                      }}
                    >
                      <div
                        className="px-6 pb-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <h3>{task.name}</h3>
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          </div>
                        </div>
                      </div>

                      {taskData && (
                        <div className="border-t border-gray-200">
                          <div className="p-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                              {renderJsonTable(taskData)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}